/* ============================================================================
   Build Your Tea Box — single-page builder
   ----------------------------------------------------------------------------
   State machine for the <tea-box-builder> element rendered by
   sections/main-tea-box-builder.liquid.

   - picks is an insertion-ordered ARRAY of {variantId, qty} — shrinking the
     box size trims from the END, matching the design handoff behavior.
   - The discount preview mirrors Shopify's per-line allocation (floor per
     line item), which was verified live to match cart totals to the cent.
     The actual discount comes from the admin automatic discounts; the
     data-discount attributes on the size cards are display mirrors only.
   - NO analytics events here — the Shopify custom pixel (204833067) owns all
     ecommerce events including add_to_cart; pushing one here double-counts.
   ========================================================================= */

class TeaBoxBuilder extends HTMLElement {
  constructor() {
    super();

    this.size = 6;
    this.discountPct = 15;
    this.picks = [];
    this.filter = 'all';
    this.adding = false;
    this.moneyFormat = window.money_format || '${{amount}}';
    this.ctaFullLabel = this.getAttribute('data-cta-full') || 'Add My Box to Cart — Ships FREE';

    this.sizeCards = Array.prototype.slice.call(this.querySelectorAll('.tb-size-card'));
    this.chips = Array.prototype.slice.call(this.querySelectorAll('.tb-chip'));
    this.teaCards = Array.prototype.slice.call(this.querySelectorAll('.tb-tea-card'));
    this.countEls = Array.prototype.slice.call(this.querySelectorAll('[data-tb-count]'));
    this.totalEls = Array.prototype.slice.call(this.querySelectorAll('[data-tb-total]'));
    this.ctaEls = Array.prototype.slice.call(this.querySelectorAll('[data-tb-cta]'));
    this.slotsEl = this.querySelector('[data-tb-slots]');
    this.progressEl = this.querySelector('[data-tb-progress]');
    this.listEl = this.querySelector('[data-tb-list]');
    this.subtotalEl = this.querySelector('[data-tb-subtotal]');
    this.discountEl = this.querySelector('[data-tb-discount]');
    this.discountLabelEl = this.querySelector('[data-tb-discount-label]');
    this.summaryEl = this.querySelector('[data-tb-summary]');
    this.toggleEl = this.querySelector('[data-tb-toggle]');

    var preselected = this.querySelector('.tb-size-card.is-selected');
    if (preselected) {
      this.size = parseInt(preselected.getAttribute('data-size'), 10) || this.size;
      this.discountPct = parseFloat(preselected.getAttribute('data-discount')) || this.discountPct;
    }

    this.presets = this.readPresets();
    this.bindEvents();
    this.applyUrlPreselect();
    this.update();
  }

  /* ---------- setup ---------- */

  readPresets() {
    var el = this.querySelector('[data-tb-presets]');
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.error('Tea Box Builder: invalid preset JSON', err);
      return [];
    }
  }

  bindEvents() {
    var self = this;

    this.sizeCards.forEach(function (card) {
      card.addEventListener('click', function () {
        self.setSize(card);
      });
    });

    this.chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        self.filter = chip.getAttribute('data-filter') || 'all';
        self.update();
      });
    });

    this.teaCards.forEach(function (card) {
      var vid = card.getAttribute('data-variant-id');
      var addBtn = card.querySelector('[data-tb-add]');
      var media = card.querySelector('[data-tb-media]');
      var minus = card.querySelector('[data-tb-minus]');
      var plus = card.querySelector('[data-tb-plus]');

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          self.addTea(vid);
        });
      }
      if (media) {
        media.addEventListener('click', function () {
          self.addTea(vid);
        });
      }
      if (minus) {
        minus.addEventListener('click', function () {
          self.removeTea(vid);
        });
      }
      if (plus) {
        plus.addEventListener('click', function () {
          self.addTea(vid);
        });
      }
    });

    this.querySelectorAll('[data-tb-preset]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var index = parseInt(btn.getAttribute('data-tb-preset'), 10);
        self.loadPreset(self.presets[index]);
      });
    });

    if (this.listEl) {
      this.listEl.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-tb-remove]');
        if (btn) self.removeTea(btn.getAttribute('data-tb-remove'));
      });
    }

    this.ctaEls.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.addToCart();
      });
    });

    if (this.toggleEl && this.summaryEl) {
      this.toggleEl.addEventListener('click', function () {
        var expanded = self.summaryEl.classList.toggle('is-expanded');
        self.toggleEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        self.toggleEl.textContent = expanded ? 'Close' : 'Details';
      });
    }
  }

  /* ---------- URL preselect (quiz handoff): ?size=4&teas=v1,v2,... ---------- */

  applyUrlPreselect() {
    var params = new URLSearchParams(window.location.search);
    var sizeParam = parseInt(params.get('size'), 10);
    var teasParam = params.get('teas');
    if (!sizeParam && !teasParam) return;

    if (sizeParam) {
      var card = this.querySelector('.tb-size-card[data-size="' + sizeParam + '"]');
      if (card) {
        this.size = sizeParam;
        this.discountPct = parseFloat(card.getAttribute('data-discount')) || 0;
      }
    }

    if (teasParam) {
      var self = this;
      this.picks = [];
      teasParam.split(',').forEach(function (vid) {
        vid = vid.trim();
        if (!vid) return;
        if (self.count() >= self.size) return;
        if (!self.cardByVariant(vid)) return;
        self.bumpPick(vid);
      });
    }

    var anchor = document.getElementById('builder');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth' });
  }

  /* ---------- state ---------- */

  count() {
    return this.picks.reduce(function (sum, p) { return sum + p.qty; }, 0);
  }

  cardByVariant(variantId) {
    return this.querySelector('.tb-tea-card[data-variant-id="' + variantId + '"]');
  }

  cardByHandle(handle) {
    return this.querySelector('.tb-tea-card[data-product-handle="' + handle + '"]');
  }

  bumpPick(variantId) {
    for (var i = 0; i < this.picks.length; i++) {
      if (this.picks[i].variantId === variantId) {
        this.picks[i].qty += 1;
        return;
      }
    }
    this.picks.push({ variantId: variantId, qty: 1 });
  }

  addTea(variantId) {
    if (this.count() >= this.size) return;
    if (!this.cardByVariant(variantId)) return;
    this.bumpPick(variantId);
    this.update();
  }

  removeTea(variantId) {
    for (var i = 0; i < this.picks.length; i++) {
      if (this.picks[i].variantId === variantId) {
        this.picks[i].qty -= 1;
        if (this.picks[i].qty <= 0) this.picks.splice(i, 1);
        break;
      }
    }
    this.update();
  }

  setSize(card) {
    this.size = parseInt(card.getAttribute('data-size'), 10) || this.size;
    this.discountPct = parseFloat(card.getAttribute('data-discount')) || 0;

    // Shrinking below the current pick count trims from the END (most
    // recently added first) — mirrors the design handoff behavior.
    var overflow = this.count() - this.size;
    for (var i = this.picks.length - 1; i >= 0 && overflow > 0; i--) {
      var take = Math.min(this.picks[i].qty, overflow);
      this.picks[i].qty -= take;
      overflow -= take;
      if (this.picks[i].qty <= 0) this.picks.splice(i, 1);
    }

    this.update();
  }

  loadPreset(preset) {
    if (!preset) return;

    var sizeCard = this.querySelector('.tb-size-card[data-size="' + preset.size + '"]');
    if (sizeCard) {
      this.size = parseInt(sizeCard.getAttribute('data-size'), 10);
      this.discountPct = parseFloat(sizeCard.getAttribute('data-discount')) || 0;
    }

    this.picks = [];
    this.filter = 'all';

    var self = this;
    (preset.handles || []).forEach(function (handle) {
      if (self.count() >= self.size) return;
      var card = self.cardByHandle(handle);
      if (!card) return; // product hidden/removed — leave the slot open
      self.bumpPick(card.getAttribute('data-variant-id'));
    });

    this.update();
  }

  /* ---------- rendering (targeted updates) ---------- */

  update() {
    this.updateSizeCards();
    this.updateFilter();
    this.updateTeaCards();
    this.updateSummary();
  }

  updateSizeCards() {
    var self = this;
    this.sizeCards.forEach(function (card) {
      var active = parseInt(card.getAttribute('data-size'), 10) === self.size;
      card.classList.toggle('is-selected', active);
      card.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  updateFilter() {
    var self = this;
    this.chips.forEach(function (chip) {
      var active = (chip.getAttribute('data-filter') || 'all') === self.filter;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    this.teaCards.forEach(function (card) {
      var hidden = self.filter !== 'all' && card.getAttribute('data-tea-filter') !== self.filter;
      card.classList.toggle('is-hidden', hidden);
    });
  }

  qtyFor(variantId) {
    for (var i = 0; i < this.picks.length; i++) {
      if (this.picks[i].variantId === variantId) return this.picks[i].qty;
    }
    return 0;
  }

  updateTeaCards() {
    var self = this;
    var full = this.count() >= this.size;

    this.teaCards.forEach(function (card) {
      var vid = card.getAttribute('data-variant-id');
      var qty = self.qtyFor(vid);
      var selected = qty > 0;

      card.classList.toggle('is-selected', selected);

      var badge = card.querySelector('[data-tb-qty-badge]');
      if (badge) badge.textContent = qty;

      var row = card.querySelector('[data-tb-stepper-row]');
      if (row) row.hidden = !selected;

      var qtyEl = card.querySelector('[data-tb-qty]');
      if (qtyEl) qtyEl.textContent = qty;

      var plus = card.querySelector('[data-tb-plus]');
      if (plus) plus.disabled = full;

      var addBtn = card.querySelector('[data-tb-add]');
      if (addBtn) {
        if (selected) {
          addBtn.textContent = 'Added';
          addBtn.disabled = true;
          addBtn.classList.add('tb-btn--outline');
        } else if (full) {
          addBtn.textContent = 'Box full';
          addBtn.disabled = true;
          addBtn.classList.remove('tb-btn--outline');
        } else {
          addBtn.textContent = 'Add';
          addBtn.disabled = false;
          addBtn.classList.remove('tb-btn--outline');
        }
      }
    });
  }

  updateSummary() {
    var self = this;
    var count = this.count();
    var full = count >= this.size;
    var pctLabel = String(this.discountPct) + '%';

    this.countEls.forEach(function (el) {
      el.textContent = count + ' of ' + self.size + ' chosen';
    });

    // Slot thumbnails — one per box slot, dashed placeholder when empty
    if (this.slotsEl) {
      var flat = [];
      this.picks.forEach(function (pick) {
        var card = self.cardByVariant(pick.variantId);
        var img = card ? card.getAttribute('data-product-image') : '';
        for (var i = 0; i < pick.qty; i++) flat.push(img);
      });
      var slotsHtml = '';
      for (var s = 0; s < this.size; s++) {
        if (flat[s]) {
          slotsHtml += '<span class="tb-summary__slot is-filled" style="background-image: url(&quot;' + flat[s] + '&quot;);"></span>';
        } else {
          slotsHtml += '<span class="tb-summary__slot"></span>';
        }
      }
      this.slotsEl.innerHTML = slotsHtml;
    }

    if (this.progressEl) {
      this.progressEl.style.width = Math.round((count / this.size) * 100) + '%';
    }

    // Chosen list — DOM built with textContent so titles never hit innerHTML
    if (this.listEl) {
      this.listEl.textContent = '';
      this.listEl.hidden = count === 0;
      this.picks.forEach(function (pick) {
        var card = self.cardByVariant(pick.variantId);
        if (!card) return;

        var li = document.createElement('li');
        li.className = 'tb-summary__item';

        var img = document.createElement('span');
        img.className = 'tb-summary__item-img';
        img.style.backgroundImage = 'url("' + card.getAttribute('data-product-image') + '")';
        li.appendChild(img);

        var title = document.createElement('span');
        title.className = 'tb-summary__item-title';
        title.textContent = card.getAttribute('data-product-title');
        li.appendChild(title);

        var qty = document.createElement('span');
        qty.className = 'tb-summary__item-qty';
        qty.textContent = '×' + pick.qty;
        li.appendChild(qty);

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'tb-summary__item-remove';
        remove.setAttribute('data-tb-remove', pick.variantId);
        remove.setAttribute('aria-label', 'Remove one ' + card.getAttribute('data-product-title'));
        remove.textContent = '×';
        li.appendChild(remove);

        self.listEl.appendChild(li);
      });
    }

    // Totals — per-line floor mirrors Shopify's discount allocation
    var subtotal = 0;
    var discount = 0;
    this.picks.forEach(function (pick) {
      var card = self.cardByVariant(pick.variantId);
      if (!card) return;
      var price = parseInt(card.getAttribute('data-product-price'), 10) || 0;
      subtotal += price * pick.qty;
      discount += Math.floor(price * self.discountPct / 100) * pick.qty;
    });

    if (this.subtotalEl) this.subtotalEl.textContent = count > 0 ? this.formatMoney(subtotal) : '–';
    if (this.discountLabelEl) this.discountLabelEl.textContent = 'Bundle discount (' + pctLabel + ')';
    if (this.discountEl) this.discountEl.textContent = count > 0 ? '− ' + this.formatMoney(discount) : '–';
    this.totalEls.forEach(function (el) {
      el.textContent = count > 0 ? self.formatMoney(subtotal - discount) : '–';
    });

    // CTA state — the compact bottom-bar CTA gets a shorter label
    var remaining = this.size - count;
    this.ctaEls.forEach(function (btn) {
      var compact = btn.classList.contains('tb-summary__bar-cta');
      btn.disabled = !full || self.adding;
      if (full) {
        btn.textContent = compact ? 'Add to Cart' : self.ctaFullLabel;
      } else {
        btn.textContent = compact
          ? 'Pick ' + remaining + ' more'
          : 'Pick ' + remaining + ' more to unlock ' + pctLabel + ' off';
      }
    });
  }

  /* ---------- cart ---------- */

  addToCart() {
    if (this.adding || this.count() !== this.size) return;

    var self = this;
    this.adding = true;
    this.ctaEls.forEach(function (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.textContent = 'Adding to cart…';
    });

    var items = this.picks.map(function (pick) {
      return { id: parseInt(pick.variantId, 10), quantity: pick.qty };
    });

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart add failed: ' + response.status);
        return response.json();
      })
      .then(function () {
        window.location.href = window.routes.root + '/cart';
      })
      .catch(function (err) {
        console.error('Tea Box Builder: Cart add failed', err);
        self.adding = false;
        self.ctaEls.forEach(function (btn) {
          btn.classList.remove('is-loading');
          btn.textContent = 'Error — try again';
          btn.disabled = false;
        });
      });
  }

  formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }
}

customElements.define('tea-box-builder', TeaBoxBuilder);
